import { forwardRef, useEffect, useId } from "react";
import { AnimatePresence, motion, useAnimationControls, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

// Adaptado do Motion Input da beUI para os campos e as cores do GSX.
export const AnimatedInput = forwardRef(function AnimatedInput({
  label,
  id: providedId,
  value,
  onChange,
  error,
  success = false,
  reserveErrorLine = false,
  leftIcon,
  rightIcon,
  endAdornment,
  className,
  classNames = {},
  required,
  ...inputProps
}, ref) {
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const controls = useAnimationControls();
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (error && !reducedMotion) {
      controls.start({ x: [0, -5, 5, -3, 3, 0], transition: { duration: 0.38 } });
    }
  }, [error, reducedMotion, controls]);

  return (
    <div className={cn("min-w-0", classNames.root)}>
      {label && (
        <label htmlFor={id} className={cn("mb-2 block text-sm font-medium text-slate-200", classNames.label)}>
          {label}{required && <span className="ml-1 text-amber-400" aria-hidden="true">*</span>}
        </label>
      )}
      <motion.div
        animate={controls}
        className={cn(
          "flex h-12 items-center rounded-xl border bg-[#0D2A52]/80 transition-[border-color,box-shadow,background-color] duration-200 focus-within:border-amber-400/70 focus-within:bg-[#102F59] focus-within:ring-2 focus-within:ring-amber-400/15",
          error ? "border-rose-400/70" : "border-white/15 hover:border-amber-400/35",
          inputProps.disabled && "opacity-60",
          classNames.field
        )}
      >
        {leftIcon && <span className={cn("ml-4 shrink-0 text-amber-400/75", classNames.leftIcon)} aria-hidden="true">{leftIcon}</span>}
        <input
          {...inputProps}
          id={id}
          ref={ref}
          required={required}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : inputProps["aria-describedby"]}
          className={cn("h-full min-w-0 flex-1 bg-transparent px-4 text-sm text-white outline-none placeholder:text-slate-500 disabled:cursor-not-allowed", leftIcon && "pl-3", className, classNames.input)}
        />
        <AnimatePresence mode="wait" initial={false}>
          {success && !error && (
            <motion.span
              key="success"
              initial={reducedMotion ? false : { opacity: 0, scale: 0.65 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reducedMotion ? undefined : { opacity: 0, scale: 0.65 }}
              className={cn("mr-4 text-amber-400", classNames.successIcon)}
              aria-label="Campo válido"
            >
              <Check className="h-4 w-4" strokeWidth={2.5} />
            </motion.span>
          )}
        </AnimatePresence>
        {rightIcon && !success && <span className={cn("mr-4 shrink-0 text-slate-400", classNames.rightIcon)} aria-hidden="true">{rightIcon}</span>}
        {endAdornment}
      </motion.div>
      <div className={cn(reserveErrorLine && "min-h-5", classNames.errorMessage)}>
        <AnimatePresence initial={false}>
          {error && (
            <motion.p
              id={errorId}
              role="alert"
              initial={reducedMotion ? false : { opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reducedMotion ? undefined : { opacity: 0, y: -3 }}
              className="pt-1 text-xs text-rose-300"
            >
              {typeof error === "string" ? error : "Confira este campo."}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});
